import { useOrganization } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";

import { useSupabaseClient } from "@/hooks/use-supabase-client";
import { useOrgProfile } from "@/hooks/use-org-profile";

// A verified/active custom domain always wins over the default subdomain
// path — matches how PublicSite.tsx itself resolves a visitor (hostname
// first, org slug as the fallback). Domain management itself lives in the
// Hub (org_domains has no editing UI in this app), but Studio still needs
// to know the resolved public URL so there's somewhere to actually send
// someone to see the live site.
export function usePublicSiteUrl(): { url: string | null; isLoading: boolean } {
  const { organization } = useOrganization();
  const { profile, loading: profileLoading } = useOrgProfile();
  const getClient = useSupabaseClient();

  const domainQuery = useQuery({
    queryKey: ["org-domains-active", organization?.id],
    enabled: Boolean(organization),
    queryFn: async (): Promise<string | null> => {
      const supabase = await getClient();
      const { data, error } = await supabase
        .from("org_domains")
        .select("domain_name, status")
        .eq("clerk_org_id", organization!.id)
        .in("status", ["verified", "active"])
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data?.domain_name ?? null;
    },
  });

  const isLoading = profileLoading || domainQuery.isLoading;
  const customDomain = domainQuery.data;

  if (customDomain) {
    return { url: `https://${customDomain}`, isLoading };
  }
  if (profile?.unit_slug) {
    return { url: `${window.location.origin}/site/${profile.unit_slug}`, isLoading };
  }
  return { url: null, isLoading };
}
